import type { TiptapContent } from '../../../../../common/tiptap/tiptapTypes';
import getTiptapNodeTextContent from '../../../../../common/tiptap/utils/jsonContentUtils/getTiptapNodeTextContent';
import { MNHotInstance } from '../../bridge/tableHotInstanceTypes';
import { MilanoteHotPlugin } from './MilanoteHotPlugin';

// Utils
import { isFormula } from '../../bridge/utils/dataUtils/formula/tableFormulaPredicateUtils';
import {
    createFormulaPickerSession,
    createFormulaPickerSessionAtCursor,
    type FormulaPickerSession,
} from '../../bridge/plugins/formulaAutocompleteSessionUtils';
import { MilanoteApplicationMode } from '../../../../../common/platform/platformTypes';
import type { FormulaPickerRef } from '../useFormulaPickerController';

export const FORMULA_AUTOCOMPLETE_PLUGIN_NAME = 'milanoteFormulaAutocomplete';

type PluginSettings = {
    formulaPickerRef: React.RefObject<FormulaPickerRef>;
};

class FormulaAutocompleteHotPlugin extends MilanoteHotPlugin<PluginSettings> {
    private formulaPickerSession: FormulaPickerSession | null;

    static get PLUGIN_KEY(): string {
        return FORMULA_AUTOCOMPLETE_PLUGIN_NAME;
    }

    constructor(hotInstance: MNHotInstance) {
        super(hotInstance);

        this.formulaPickerSession = null;
    }

    /**
     * Enable the plugin.
     */
    enablePlugin(): void {
        this.addHook('afterBeginEditing', () => this.onAfterBeginEditing());
        this.addHook('afterSelection', () => this.onAfterSelection());

        this.addMilanoteHook('onMilanoteCellEditorChange', (content: TiptapContent) =>
            this.onMilanoteCellEditorChange(content),
        );
        this.addMilanoteHook('onMilanoteCellEditorSelectionChange', () => this.onMilanoteCellEditorSelectionChange());

        super.enablePlugin();
    }

    /**
     * Update the plugin.
     */
    updatePlugin(): void {
        this.disablePlugin();
        this.enablePlugin();

        super.updatePlugin();
    }

    /******************
     * PLUGIN HOOKS
     ******************/

    onAfterBeginEditing(): void {
        this.formulaPickerSession = null;
    }

    onAfterSelection(): void {
        this.removeFormulaPicker();
    }

    onMilanoteCellEditorChange(content: TiptapContent): void {
        if (!this.shouldRespondToCellEditorChanges()) return;

        const cellText = getTiptapNodeTextContent(content);
        const selection = this.milanoteHot.getMNEditor()?.getPlainTextSelection();

        this.syncPickerToSelection(cellText, selection?.from);
    }

    onMilanoteCellEditorSelectionChange(): void {
        if (!this.shouldRespondToCellEditorChanges()) return;

        const selection = this.milanoteHot.getMNEditor()?.getPlainTextSelection();
        const cellText = this.milanoteHot.getMNEditor()?.getPlainTextContent() ?? '';
        this.syncPickerToSelection(cellText, selection?.from);
    }

    /******************
     * HELPER FUNCTIONS
     ******************/

    openFormulaPicker(cellContent: string): void {
        const selection = this.milanoteHot.getMNEditor()?.getPlainTextSelection();
        this.formulaPickerSession = createFormulaPickerSession(cellContent, selection?.from ?? cellContent.length);
        this.updateFormulaPickerForCellText(cellContent, selection?.from ?? cellContent.length);
    }

    updateFormulaPickerForCellText(newCellContent: string, cursorOffset: number): void {
        const { formulaPickerRef } = this.getConfig();
        if (!formulaPickerRef?.current) return;

        if (!this.formulaPickerSession || cursorOffset < this.formulaPickerSession.queryStartOffset) {
            return this.removeFormulaPicker();
        }

        const query = newCellContent.slice(this.formulaPickerSession.queryStartOffset, cursorOffset);

        this.formulaPickerSession = {
            ...this.formulaPickerSession,
            queryEndOffset: cursorOffset,
        };

        formulaPickerRef.current.setFormulaPickerQuery(query);
    }

    /**
     * Keeps the formula picker aligned with the current formula token under the caret.
     */
    private syncPickerToSelection(cellText: string, cursorOffset?: number): void {
        if (!isFormula(cellText) || cursorOffset === undefined) return this.removeFormulaPicker();

        const session = createFormulaPickerSessionAtCursor(cellText, cursorOffset);
        if (!session) return this.removeFormulaPicker();

        this.formulaPickerSession = session;
        this.updateFormulaPickerForCellText(cellText, cursorOffset);
    }

    removeFormulaPicker(): void {
        const { formulaPickerRef } = this.getConfig();
        if (!formulaPickerRef?.current) return;

        this.formulaPickerSession = null;

        formulaPickerRef.current.hideFormulaPicker();
    }

    /**
     * Only respond to cell editor changes on non-mobile devices.
     * On mobile, the formula sheet has its own search input.
     */
    shouldRespondToCellEditorChanges(): boolean {
        return this.milanoteHot.milanoteProps?.appMode !== MilanoteApplicationMode.mobile;
    }

    /**
     * Destroy the plugin.
     */
    destroy(): void {
        this.removeFormulaPicker();
        super.destroy();
    }
}

export default FormulaAutocompleteHotPlugin;
